/* global google */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer, StreetViewPanorama, useLoadScript } from '@react-google-maps/api';
import { FaDirections, FaLocationArrow, FaRedo, FaSearch, FaStreetView } from 'react-icons/fa';
import ShopMarker from '../Map/Marker'; 
import { server } from '../../server';
import { GOOGLE_MAPS_LIBRARIES } from '../../constants/googleMaps';

const initialCenter = {
  lat: 34.0549, // Default latitude
  lng: -118.2426, // Default longitude
};

const GEOLOCATION_PERMISSION_DENIED = 1;
const GEOLOCATION_POSITION_UNAVAILABLE = 2;
const GEOLOCATION_TIMEOUT = 3;

const formatEventDate = (event) => {
  if (!event?.date) {
    return 'Date pending';
  }

  const eventDate = new Date(event.date);
  return Number.isNaN(eventDate.getTime())
    ? 'Date pending'
    : eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatEventTime = (event) => {
  if (event?.startTime && event?.endTime) {
    return `${event.startTime} - ${event.endTime}`;
  }

  return event?.startTime || '';
};

const EventMapCard = ({ event }) => (
  <article className="map-event-marker-card">
    {event?.image ? (
      <img className="map-event-marker-card__image" src={event.image} alt={event.title || 'Event'} />
    ) : null}
    <div className="map-event-marker-card__body">
      <h3>{event?.title || 'Event'}</h3>
      <p>{formatEventDate(event)}{formatEventTime(event) ? ` at ${formatEventTime(event)}` : ''}</p>
      <p>{event?.location?.address || 'Event location'}</p>
      <a href={`/events/${event?._id}`} className="map-event-marker-card__link">View Event</a>
    </div>
  </article>
);

const MapComponent = ({
  selectedBrand,
  selectedShopList,
  selectedEventList,
  activeMapContent = 'shops',
  onVisibleEventsChange,
}) => {
  const [shops, setShops] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentRegion, setCurrentRegion] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchCenter, setActiveSearchCenter] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [directions, setDirections] = useState(null);
  const [directionsError, setDirectionsError] = useState('');
  const [showStreetView, setShowStreetView] = useState(false);
  const [streetViewPosition, setStreetViewPosition] = useState(initialCenter);
  const mapRef = React.useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const customMarkerIcon = isLoaded ? {
    url: 'https://res.cloudinary.com/dzlopmfj8/image/upload/v1703164410/BuzzWeed_Map_Pin_yrteuc.svg',
    scaledSize: new window.google.maps.Size(80, 80),
  } : null;

  const eventMarkerIcon = isLoaded ? {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: '#f97316',
    fillOpacity: 0.95,
    strokeColor: '#ffffff',
    strokeWeight: 3,
    scale: 11,
  } : null;

  const getShopLatLng = useCallback((shop) => ({
    lat: parseFloat(shop?.latitude),
    lng: parseFloat(shop?.longitude),
  }), []);

  const parseCoordinate = useCallback((value) => {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      return Number(value);
    }

    return NaN;
  }, []);

  const getEventLatLng = useCallback((event) => ({
    lat: parseCoordinate(event?.location?.lat),
    lng: parseCoordinate(event?.location?.lng),
  }), [parseCoordinate]);

  const isValidLatLng = useCallback((coords) => (
    Boolean(coords) && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)
  ), []);

  const haversineDistanceMiles = useCallback((pointA, pointB) => {
    const toRadians = (value) => (value * Math.PI) / 180;
    const earthRadiusMiles = 3958.8;
    const latDiff = toRadians(pointB.lat - pointA.lat);
    const lngDiff = toRadians(pointB.lng - pointA.lng);
    const a =
      Math.sin(latDiff / 2) * Math.sin(latDiff / 2) +
      Math.cos(toRadians(pointA.lat)) *
        Math.cos(toRadians(pointB.lat)) *
        Math.sin(lngDiff / 2) *
        Math.sin(lngDiff / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMiles * c;
  }, []);

  const geocodeEventAddress = useCallback((geocoder, event) => new Promise((resolve) => {
    const address = event?.location?.address;
    if (!address || typeof address !== 'string' || !address.trim() || address.trim() === 'Event Location') {
      resolve(null);
      return;
    }

    geocoder.geocode({ address: address.trim() }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const location = results[0].geometry.location;
        resolve({ lat: location.lat(), lng: location.lng() });
        return;
      }

      resolve(null);
    });
  }), []);

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const url = selectedBrand
          ? `${server}/shop/all-sellers?brand=${selectedBrand.value}`
          : `${server}/shop/all-sellers`;
        const response = await axios.get(url, { withCredentials: true });
        setShops(response.data.sellers);
      } catch (error) {
        console.error('Error fetching shops:', error);
      }
    };
    fetchShops();
  }, [selectedBrand]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${server}/event/events`, { withCredentials: true });
        setEvents(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching map events:', error);
        setEvents([]);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    if (!isLoaded || !events.length || typeof google === 'undefined') {
      return undefined;
    }

    const eventsMissingCoordinates = events.filter((event) => (
      !isValidLatLng(getEventLatLng(event))
      && typeof event?.location?.address === 'string'
      && event.location.address.trim()
    ));

    if (!eventsMissingCoordinates.length) {
      return undefined;
    }

    let isCancelled = false;
    const geocoder = new google.maps.Geocoder();

    const resolveMissingCoordinates = async () => {
      const coordinateUpdates = new Map();

      for (const event of eventsMissingCoordinates.slice(0, 20)) {
        const resolvedCoordinates = await geocodeEventAddress(geocoder, event);
        if (resolvedCoordinates) {
          coordinateUpdates.set(String(event._id), resolvedCoordinates);
        }
      }

      if (isCancelled || !coordinateUpdates.size) {
        return;
      }

      setEvents((currentEvents) => currentEvents.map((event) => {
        const resolvedCoordinates = coordinateUpdates.get(String(event._id));
        if (!resolvedCoordinates) {
          return event;
        }

        return {
          ...event,
          location: {
            ...(event.location || {}),
            lat: resolvedCoordinates.lat,
            lng: resolvedCoordinates.lng,
          },
        };
      }));
    };

    resolveMissingCoordinates();

    return () => {
      isCancelled = true;
    };
  }, [events, geocodeEventAddress, getEventLatLng, isLoaded, isValidLatLng]);

  useEffect(() => {
    if (activeMapContent === 'events') {
      setSelectedShop(null);
      return;
    }

    setSelectedEvent(null);
  }, [activeMapContent]);

  useEffect(() => {
    if (selectedShopList) {
      const shopCoords = getShopLatLng(selectedShopList);
      if (Number.isNaN(shopCoords.lat) || Number.isNaN(shopCoords.lng)) {
        return;
      }

      setSelectedShop(selectedShopList);
      setDirectionsError('');
      setStreetViewPosition(shopCoords);

      if (mapRef.current) {
        mapRef.current.panTo(shopCoords);
        mapRef.current.setZoom(12);
      }
    }
  }, [getShopLatLng, selectedShopList]);

  useEffect(() => {
    if (selectedEventList) {
      const eventCoords = getEventLatLng(selectedEventList);
      if (!isValidLatLng(eventCoords)) {
        return;
      }

      setSelectedEvent(selectedEventList);
      setDirectionsError('');
      setStreetViewPosition(eventCoords);

      if (mapRef.current) {
        mapRef.current.panTo(eventCoords);
        mapRef.current.setZoom(12);
      }
    }
  }, [getEventLatLng, isValidLatLng, selectedEventList]);

  const nearbyShops = useMemo(() => (
    activeSearchCenter
      ? shops.filter((shop) => {
        const shopCoords = getShopLatLng(shop);
        if (Number.isNaN(shopCoords.lat) || Number.isNaN(shopCoords.lng)) {
          return false;
        }
        return haversineDistanceMiles(activeSearchCenter, shopCoords) <= 30;
      })
      : shops
  ), [activeSearchCenter, getShopLatLng, haversineDistanceMiles, shops]);

  const nearbyEvents = useMemo(() => {
    const mappableEvents = events.filter((event) => isValidLatLng(getEventLatLng(event)));

    if (!activeSearchCenter) {
      return mappableEvents;
    }

    return mappableEvents.filter((event) => haversineDistanceMiles(activeSearchCenter, getEventLatLng(event)) <= 30);
  }, [activeSearchCenter, events, getEventLatLng, haversineDistanceMiles, isValidLatLng]);

  useEffect(() => {
    onVisibleEventsChange?.(activeMapContent === 'events' ? nearbyEvents : []);
  }, [activeMapContent, nearbyEvents, onVisibleEventsChange]);

  const geocodeLatLng = (geocoder, latLng) => {
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const placeName = results.find(result => result.types.includes('locality'))?.formatted_address;
        setCurrentRegion(placeName || "Unknown location");
      } else {
        console.log("Geocoder failed due to: " + status);
      }
    });
  };

  const getGeolocationErrorMessage = (error) => {
    switch (error?.code) {
      case GEOLOCATION_PERMISSION_DENIED:
        return 'Location access is blocked in this browser. Enable location permissions or search manually.';
      case GEOLOCATION_POSITION_UNAVAILABLE:
        return 'Current location is unavailable right now. Try again or search manually.';
      case GEOLOCATION_TIMEOUT:
        return 'Location request timed out. Try again or search manually.';
      default:
        return 'Unable to access your current location.';
    }
  };

  const requestUserLocation = (onSuccess, onFailure) => {
    if (!navigator.geolocation) {
      onFailure('Geolocation is not available on this device.');
      return;
    }

    const handleSuccess = (position) => {
      onSuccess({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    };

    const handleFailure = (error) => {
      onFailure(getGeolocationErrorMessage(error));
    };

    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      (error) => {
        if (error?.code === GEOLOCATION_PERMISSION_DENIED) {
          handleFailure(error);
          return;
        }

        navigator.geolocation.getCurrentPosition(
          handleSuccess,
          handleFailure,
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
        );
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  const openExternalDirections = (destination) => {
    if (typeof window === 'undefined' || !isValidLatLng(destination)) {
      return;
    }

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${destination.lat},${destination.lng}`)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const getPreferredStreetViewPosition = () => {
    const selectedShopCoords = selectedShop ? getShopLatLng(selectedShop) : null;
    if (isValidLatLng(selectedShopCoords)) {
      return selectedShopCoords;
    }

    const selectedEventCoords = selectedEvent ? getEventLatLng(selectedEvent) : null;
    if (isValidLatLng(selectedEventCoords)) {
      return selectedEventCoords;
    }

    const listedShopCoords = selectedShopList ? getShopLatLng(selectedShopList) : null;
    if (isValidLatLng(listedShopCoords)) {
      return listedShopCoords;
    }

    if (isValidLatLng(userLocation)) {
      return userLocation;
    }

    if (isValidLatLng(activeSearchCenter)) {
      return activeSearchCenter;
    }

    if (isValidLatLng(streetViewPosition)) {
      return streetViewPosition;
    }

    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      if (center) {
        return { lat: center.lat(), lng: center.lng() };
      }
    }

    return initialCenter;
  };

  const performInitialSearch = (map) => {
    const center = map.getCenter();
    if (!center) {
      return;
    }

    const centerCoords = { lat: center.lat(), lng: center.lng() };
    setStreetViewPosition(centerCoords);
    const geocoder = new google.maps.Geocoder();
    geocodeLatLng(geocoder, centerCoords);
  };

  const redoSearchInArea = () => {
    if (!mapRef.current || !isLoaded) return;
    const center = mapRef.current.getCenter();
    if (!center) {
      return;
    }

    const centerCoords = { lat: center.lat(), lng: center.lng() };
    setDirections(null);
    setDirectionsError('');
    setSelectedShop(null);
    setSelectedEvent(null);
    setActiveSearchCenter(centerCoords);
    setStreetViewPosition(centerCoords);
    const geocoder = new google.maps.Geocoder();
    geocodeLatLng(geocoder, centerCoords);
  };

  const searchLocation = () => {
    if (!mapRef.current || !isLoaded || !searchQuery.trim()) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery.trim() }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        mapRef.current.panTo(location);
        mapRef.current.setZoom(12);
        const latLng = { lat: location.lat(), lng: location.lng() };
        setDirections(null);
        setDirectionsError('');
        setSelectedShop(null);
        setSelectedEvent(null);
        setActiveSearchCenter(latLng);
        setStreetViewPosition(latLng);
        geocodeLatLng(geocoder, latLng);
      } else {
        console.log('Address search failed due to: ' + status);
      }
    });
  };

  const useMyLocation = () => {
    if (!mapRef.current) {
      return;
    }

    requestUserLocation(
      (coords) => {
        setSelectedShop(null);
        setSelectedEvent(null);
        setDirections(null);
        setDirectionsError('');
        setUserLocation(coords);
        setActiveSearchCenter(coords);
        setStreetViewPosition(coords);
        mapRef.current.panTo(coords);
        mapRef.current.setZoom(12);
        const geocoder = new google.maps.Geocoder();
        geocodeLatLng(geocoder, coords);
      },
      (message) => {
        setDirectionsError(message);
      }
    );
  };

  const getDirections = () => {
    if (!isLoaded) return;
    const destinationShop = selectedShop || selectedShopList;
    const destinationEvent = selectedEvent || selectedEventList;
    const destination = destinationShop
      ? getShopLatLng(destinationShop)
      : destinationEvent
        ? getEventLatLng(destinationEvent)
      : activeSearchCenter;

    if (!destination) {
      setDirectionsError('Select a shop, event marker, or search a location first.');
      return;
    }

    const routeFromCurrentLocation = (origin) => {
      const directionsService = new google.maps.DirectionsService();
      directionsService.route(
        {
          origin,
          destination,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK' && result) {
            setDirections(result);
            setDirectionsError('');
          } else {
            openExternalDirections(destination);
            setDirectionsError('Could not fetch directions for this route. Opening directions in Google Maps instead.');
          }
        }
      );
    };

    if (userLocation) {
      routeFromCurrentLocation(userLocation);
      return;
    }

    requestUserLocation(
      (origin) => {
        setUserLocation(origin);
        routeFromCurrentLocation(origin);
      },
      (message) => {
        openExternalDirections(destination);
        setDirectionsError(`${message} Opening directions in Google Maps instead.`);
      }
    );
  };

  const toggleStreetView = () => {
    if (showStreetView) {
      setShowStreetView(false);
      return;
    }

    if (!isLoaded) {
      return;
    }

    const preferredPosition = getPreferredStreetViewPosition();
    if (!isValidLatLng(preferredPosition)) {
      setDirectionsError('Search a location, choose a shop, or use your location to open Street View.');
      return;
    }

    const streetViewService = new google.maps.StreetViewService();
    streetViewService.getPanorama(
      { location: preferredPosition, radius: 1000 },
      (data, status) => {
        if (status === google.maps.StreetViewStatus.OK && data?.location?.latLng) {
          const panoramaPosition = {
            lat: data.location.latLng.lat(),
            lng: data.location.latLng.lng(),
          };
          setStreetViewPosition(panoramaPosition);
          setDirectionsError('');
          setShowStreetView(true);
        } else {
          setShowStreetView(false);
          setDirectionsError('Street View is unavailable for this location.');
        }
      }
    );
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      searchLocation();
    }
  };

  if (loadError) return "Error loading maps";
  if (!isLoaded) return "Loading Maps";

  return (
    <>
      <div className="map-component-container">
        <h2 className="map-component-title">
          {currentRegion || "Loading..."}
        </h2>
        <div className="map-component-search-row">
          <input
            type="text"
            className="map-component-search-input"
            placeholder="Search location (city, address, zip)"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <button
            type="button"
            onClick={searchLocation}
            className="map-component-button map-component-tool-button"
            aria-label="Search map location"
          >
            <FaSearch aria-hidden="true" />
            <span>Search</span>
          </button>
          <button
            type="button"
            onClick={useMyLocation}
            className="map-component-button map-component-button-secondary map-component-tool-button"
            aria-label="Use my location"
          >
            <FaLocationArrow aria-hidden="true" />
            <span>Use My Location</span>
          </button>
          <button
            type="button"
            onClick={getDirections}
            className="map-component-button map-component-button-secondary map-component-tool-button"
            aria-label="Get directions"
          >
            <FaDirections aria-hidden="true" />
            <span>Directions</span>
          </button>
          <button
            type="button"
            onClick={toggleStreetView}
            className="map-component-button map-component-button-secondary map-component-tool-button"
            aria-label={showStreetView ? 'Hide Street View' : 'Open Street View'}
          >
            <FaStreetView aria-hidden="true" />
            <span>{showStreetView ? 'Hide Street View' : 'Street View'}</span>
          </button>
          <button
            type="button"
            onClick={redoSearchInArea}
            className="map-component-button map-component-button-secondary map-component-tool-button"
            aria-label="Redo search in this area"
          >
            <FaRedo aria-hidden="true" />
            <span>Redo Area</span>
          </button>
        </div>
        {activeSearchCenter && (
          <p className="map-component-helper-text">
            Showing {activeMapContent === 'events' ? nearbyEvents.length : nearbyShops.length} nearby {activeMapContent === 'events' ? 'event' : 'location'}{(activeMapContent === 'events' ? nearbyEvents.length : nearbyShops.length) === 1 ? '' : 's'} within 30 miles of this search.
          </p>
        )}
        {directionsError && <p className="map-component-error-text">{directionsError}</p>}
      </div>
      <GoogleMap
        mapContainerClassName="map-component"
        zoom={8}
        center={initialCenter}
        onLoad={(map) => {
          mapRef.current = map;
          performInitialSearch(map);
        }}
      >
        {activeMapContent !== 'events' && nearbyShops.map((shop, index) => {
          const shopCoords = getShopLatLng(shop);
          if (Number.isNaN(shopCoords.lat) || Number.isNaN(shopCoords.lng)) {
            return null;
          }
          return (
          <Marker
            key={index}
            position={shopCoords}
            icon={customMarkerIcon}
            onClick={() => {
              setSelectedShop(shop);
              setDirectionsError('');
              setStreetViewPosition(shopCoords);
            }}
          />
          );
        })}

        {activeMapContent === 'events' && nearbyEvents.map((event) => {
          const eventCoords = getEventLatLng(event);

          return (
            <Marker
              key={event._id}
              position={eventCoords}
              icon={eventMarkerIcon}
              onClick={() => {
                setSelectedEvent(event);
                setDirectionsError('');
                setStreetViewPosition(eventCoords);
              }}
            />
          );
        })}

        {userLocation && (
          <Marker
            position={userLocation}
            onClick={() => setSelectedShop(null)}
            label="You"
          />
        )}

        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{ suppressMarkers: false }}
          />
        )}

        {showStreetView && streetViewPosition && (
          <StreetViewPanorama
            position={streetViewPosition}
            visible
            options={{
              enableCloseButton: false,
              fullscreenControl: true,
              zoomControl: true,
            }}
          />
        )}

        {selectedShop && (
          <InfoWindow
            position={getShopLatLng(selectedShop)}
            onCloseClick={() => setSelectedShop(null)}
          >
            <ShopMarker shop={selectedShop} />
          </InfoWindow>
        )}
        {selectedEvent && (
          <InfoWindow
            position={getEventLatLng(selectedEvent)}
            onCloseClick={() => setSelectedEvent(null)}
          >
            <EventMapCard event={selectedEvent} />
          </InfoWindow>
        )}
        {selectedShopList && (
          <InfoWindow
            position={{ lat: parseFloat(selectedShopList.latitude), lng: parseFloat(selectedShopList.longitude) }}
            onCloseClick={() => setSelectedShop(null)}
          >
            <div>
              <ShopMarker shop={selectedShopList} />
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </>
  );
};

export default MapComponent;




